import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "../styles/ProjectPage.css";
import axios from "axios";
import AppCard from "../components/AppCard";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../components/Loader";

interface Projects {
  id: number;
  name: string;
  description: string;
  image: string;
}

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Projects[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [backend] = useState<string>(
    "https://personal-portfolio-backend-waf6.vercel.app"
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({
      duration: 500, // Animation duration in milliseconds (can be adjusted)
      offset: 200, // Trigger animation when 200px from the top
      easing: "ease-in-out", // Easing function
      once: true, // Animation runs only once
      mirror: true, // Play animation again when scrolling back up
    });

    const fetchProjects = async (
      retryCount = 0,
      retryLimit = 60,
      retryDelay = 2000
    ) => {
      try {
        const response = await axios.get(backend + "/content/projects.json");
        if (response.data) {
          setProjects(response.data);
          setLoading(false); // Stop retrying after success
        } else {
          setError("No projects found.");
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        if (retryCount < retryLimit) {
          setTimeout(() => fetchProjects(retryCount + 1), retryDelay);
        } else {
          setError("Failed to fetch projects. Please try again later...");
          setLoading(false);
        }
      }
    };

    fetchProjects();
  }, [backend]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimationStarted(true);
    }, 500);

    return () => clearTimeout(timer); // Clear timeout on unmount
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? projects.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === projects.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleDotClick = (index: number): void => {
    setCurrentIndex(index);
  };

  return (
    <div className="projectPage">
      <div className="title" data-aos="fade-up">
        My recent projects
      </div>
      {error && <p className="message">{error}</p>}

      {loading ? (
        <div className="projectsLoader" data-aos="fade-up">
          <Loader />
        </div>
      ) : (
        projects.length > 0 && (
          <>
            <div className="cardContainer">
              <AnimatePresence mode="wait">
                {hasAnimationStarted && (
                  <motion.div
                    key={currentIndex} // Unique key to trigger animation
                    initial={{ opacity: 0, scale: 0.9, x: 0 }} // Start off-screen
                    animate={{ opacity: 1, scale: 1, x: 0 }} // Slide into place
                    exit={{ opacity: 0, scale: 0.9, x: 0 }} // Slide out
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                      duration: 0.5,
                    }}
                    className="motionCard"
                    data-aos="zoom-in" // Added AOS fade-up effect
                  >
                    <AppCard
                      id={projects[currentIndex].id}
                      appName={projects[currentIndex].name}
                      appDescription={projects[currentIndex].description}
                      appImage={`${backend}/photos/${projects[currentIndex].image}`}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div
              className="carouselButtons"
              data-aos="zoom-in"
              data-aos-offset="100"
              data-aos-delay="500"
            >
              <button className="carouselButton prev" onClick={handlePrev}>
                <ChevronLeft />
              </button>
              <div className="dots">
                {projects.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${index === currentIndex ? "active" : ""}`}
                    onClick={() => handleDotClick(index)}
                  ></span>
                ))}
              </div>
              <button className="carouselButton next" onClick={handleNext}>
                <ChevronRight />
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default ProjectsPage;
